import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  FileAudio, 
  CheckCircle, 
  AlertCircle, 
  Loader2, 
  Clock,
  Trash2,
  History
} from 'lucide-react';
import { Transcription } from '@/types/transcription';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface TranscriptionHistoryProps {
  transcriptions: Transcription[];
  selectedId: string | null;
  onSelect: (transcription: Transcription) => void;
  onDelete: (id: string) => void;
  isLoading: boolean;
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'failed':
      return <AlertCircle className="h-4 w-4 text-destructive" />;
    case 'processing':
    case 'uploading':
    case 'downloading':
    case 'transcribing':
    case 'analyzing':
    case 'analyzing_attempt_1':
    case 'analyzing_attempt_2':
    case 'analyzing_attempt_3':
      return <Loader2 className="h-4 w-4 text-primary animate-spin" />;
    default:
      return <Clock className="h-4 w-4 text-muted-foreground" />;
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'completed': return 'Voltooid';
    case 'failed': return 'Mislukt';
    case 'uploading': return 'Uploaden...';
    case 'downloading': return 'Downloaden...';
    case 'transcribing': return 'Transcriberen...';
    case 'analyzing':
    case 'analyzing_attempt_1':
    case 'analyzing_attempt_2':
    case 'analyzing_attempt_3':
      return 'Analyseren...';
    default: return 'Verwerken...';
  }
};

export const TranscriptionHistory = ({ 
  transcriptions, 
  selectedId, 
  onSelect, 
  onDelete,
  isLoading 
}: TranscriptionHistoryProps) => {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">Laden...</p>
        </CardContent>
      </Card>
    );
  }

  if (transcriptions.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Nog geen transcripties.</p>
          <p className="text-sm mt-1">Upload een audiobestand om te beginnen.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <History className="h-5 w-5" />
          Geschiedenis ({transcriptions.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-[500px]">
          <div className="divide-y">
            {transcriptions.map((transcription) => (
              <div
                key={transcription.id}
                className={cn(
                  "flex items-center gap-3 p-4 cursor-pointer hover:bg-muted/50 transition-colors",
                  selectedId === transcription.id && "bg-primary/10 hover:bg-primary/10"
                )}
                onClick={() => onSelect(transcription)}
              >
                <FileAudio className="h-5 w-5 text-muted-foreground shrink-0" />
                
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{transcription.filename}</p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(transcription.created_at), "d MMM yyyy HH:mm", { locale: nl })}
                  </p>
                </div>
                
                <div className="flex items-center gap-2 shrink-0">
                  <Badge 
                    variant={transcription.status === 'completed' ? 'default' : 
                             transcription.status === 'failed' ? 'destructive' : 'secondary'}
                    className={cn(
                      "text-xs",
                      transcription.status === 'completed' && "bg-green-500/20 text-green-500 border-green-500/30"
                    )}
                  >
                    <span className="flex items-center gap-1">
                      {getStatusIcon(transcription.status)}
                      <span className="hidden sm:inline">{getStatusLabel(transcription.status)}</span>
                    </span>
                  </Badge>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Transcriptie verwijderen?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Weet je zeker dat je "{transcription.filename}" wilt verwijderen? 
                          Dit kan niet ongedaan worden gemaakt.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annuleren</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => onDelete(transcription.id)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Verwijderen
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};
